import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Input,
} from '@you/ui'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useState } from 'react'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { UpdateSpace } from '@/components/Sidebar/UpdateSpace'
import { Icon } from '@/components/Icon'
import {
  Workspace,
  useWorkspaces,
  useTransferWorkspace,
  useDeleteWorkspace,
  useLeaveWorkspace,
  useWorkspaceMembers,
} from '@/api'
import { useNavigate } from 'react-router-dom'
import { WorkspaceType } from '@/api/types/workspace.types'
import { OrgTag } from './apps'

const Setting = () => {
  const navigate = useNavigate()
  const { currentWorkspace, setCurrentWorkspace } = useWorkspaceStore()
  const { mutate: refreshWorkspaces } = useWorkspaces()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false)
  const [selectedMemberId, setSelectedMemberId] = useState<string>('')
  const [deleteConfirmName, setDeleteConfirmName] = useState('')
  const isPersonalSpace = currentWorkspace?.type === WorkspaceType.Personal
  const isOrgSpace = currentWorkspace?.type === WorkspaceType.Organization

  // 获取成员列表
  const {
    data: membersData,
    isLoading: isLoadingMembers,
    mutate: refreshMembers,
  } = useWorkspaceMembers({
    workspaceId: Number(currentWorkspace?.id),
  })

  // 获取当前用户在成员列表中的信息
  const currentMember = membersData?.members?.find(
    (member) => member.isCurrentUser === 1
  )
  const isOwner = currentMember?.isOwner === 1

  // 转让、删除、退出的hooks
  const { trigger: transferWorkspace } = useTransferWorkspace()
  const { trigger: deleteWorkspace } = useDeleteWorkspace()
  const { trigger: leaveWorkspace } = useLeaveWorkspace()

  // 更新成功后，重新获取工作空间列表以更新当前空间信息
  const handleSuccess = async (data: unknown) => {
    setDialogOpen(false)
    await refreshWorkspaces()
    console.log('空间更新成功，已刷新工作空间列表', data)
  }

  // 处理转让空间
  const handleTransfer = async () => {
    if (!currentWorkspace || !selectedMemberId) return

    try {
      await transferWorkspace({
        workspaceId: currentWorkspace.id,
        toUserId: selectedMemberId,
      })

      await refreshWorkspaces()
      await refreshMembers() // 重新获取成员列表以更新权限状态
      setTransferDialogOpen(false)
    } catch (error) {
      console.error('转让空间失败:', error)
    }
  }

  // 处理删除空间
  const handleDelete = async () => {
    if (!currentWorkspace) return

    try {
      await deleteWorkspace({
        id: currentWorkspace.id,
      })

      await refreshWorkspaces()
      setDeleteDialogOpen(false)
      setCurrentWorkspace(null)
      navigate('/workspace')
    } catch (error) {
      console.error('删除空间失败:', error)
    }
  }

  // 处理退出空间
  const handleLeave = async () => {
    if (!currentWorkspace) return

    try {
      await leaveWorkspace({
        id: currentWorkspace.id,
      })

      await refreshWorkspaces()
      setLeaveDialogOpen(false)
      setCurrentWorkspace(null)
      navigate('/workspace')
    } catch (error) {
      console.error('退出空间失败:', error)
    }
  }

  return (
    <div className="space-y-6 px-8 py-4">
      {/* 工作空间信息展示 */}
      <div className="flex items-center gap-4">
        <Avatar className="w-[70px] h-[70px]">
          <AvatarImage
            src={currentWorkspace?.icon}
            alt={currentWorkspace?.name}
          />
          <AvatarFallback className="text-2xl font-medium">
            {currentWorkspace?.name.slice(0, 1)}
          </AvatarFallback>
        </Avatar>

        <div className="flex items-center gap-2">
          <h2
            className="text-2xl font-bold relative min-w-[100px] text-center"
            style={{ color: '#3D1E82' }}
          >
            {currentWorkspace?.name}
            {/* Tag 标签 - 绝对定位居中 */}
            {!isPersonalSpace && !isLoadingMembers && currentMember && (
              <p
                className="absolute left-1/2 transform -translate-x-1/2 px-2 top-full text-[10px] rounded-[20px] h-4 leading-4 font-medium text-nowrap"
                style={{
                  backgroundColor: '#8752FF2E',
                  color: '#8752FF',
                }}
              >
                我的角色：{isOwner ? '管理员' : '成员'}
              </p>
            )}
          </h2>

          {/* 组织空间认证 tag */}
          {isOrgSpace && <OrgTag />}

          {/* 编辑按钮：始终显示，组织空间仅允许改头像 */}
          <Button
            variant="ghost"
            size="sm"
            className="p-1 h-auto"
            onClick={() => setDialogOpen(true)}
          >
            <Icon name="edit" className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* 空间管理功能区 */}
      {!isPersonalSpace && (
        <div className="space-y-6 mt-8">
          {isLoadingMembers ? (
            // 加载状态下显示骨架屏
            <div className="space-y-6">
              <div className="space-y-2">
                <Skeleton className="h-7 w-32" /> {/* 标题骨架 */}
                <Skeleton className="h-5 w-48" /> {/* 描述文本骨架 */}
                <Skeleton className="h-10 w-32" /> {/* 按钮骨架 */}
              </div>
              <div className="space-y-2">
                <Skeleton className="h-7 w-32" />
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-10 w-32" />
              </div>
            </div>
          ) : (
            currentMember && (
              <>
                {/* 转让空间和删除空间 - 仅管理员可见 */}
                {isOwner && (
                  <>
                    <div className="space-y-2">
                      <h3 className="text-lg font-medium">转让空间</h3>
                      <p className="text-sm text-gray-500">
                        将空间所有权转移给其他成员
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => setTransferDialogOpen(true)}
                      >
                        转让空间给
                      </Button>
                    </div>

                    {/* 组织空间不允许删除，由渠道后台管理 */}
                    {!isOrgSpace && (
                      <div className="space-y-2">
                        <h3 className="text-lg font-medium">删除空间</h3>
                        <p className="text-sm text-gray-500">
                          删除空间后，空间内所有内容全部清空
                        </p>
                        <Button
                          variant="destructive"
                          onClick={() => setDeleteDialogOpen(true)}
                        >
                          删除空间
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {/* 退出空间 - 仅普通成员可见 */}
                {!isOwner && (
                  <div className="space-y-2">
                    <h3 className="text-lg font-medium">退出空间</h3>
                    <p className="text-sm text-gray-500">
                      退出后将无法访问空间内容
                    </p>
                    <Button
                      variant="destructive"
                      onClick={() => setLeaveDialogOpen(true)}
                    >
                      退出空间
                    </Button>
                  </div>
                )}
              </>
            )
          )}
        </div>
      )}

      {/* 编辑工作空间对话框 */}
      <UpdateSpace
        mode="edit"
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        workspace={currentWorkspace as Workspace}
        successCallback={handleSuccess}
        closeDialog={() => setDialogOpen(false)}
        disableName={isOrgSpace}
      />

      {/* 转让空间对话框 */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>转让空间</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 p-6">
            <p className="text-m text-[#F99E83]">
              请选择要转让给的成员。转让后，您的状态将更改为普通成员。
            </p>
            <p className="text-m text-black">
              将「{currentWorkspace?.name}」空间所有权转让给
            </p>
            <Select
              value={selectedMemberId}
              onValueChange={setSelectedMemberId}
            >
              <SelectTrigger>
                <SelectValue placeholder="选择成员" />
              </SelectTrigger>
              <SelectContent>
                {membersData?.members
                  ?.filter((member) => !member.isOwner)
                  .map((member) => (
                    <SelectItem key={member.userId} value={member.userId || ''}>
                      {member.userName}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTransferDialogOpen(false)}
            >
              取消
            </Button>
            <Button onClick={handleTransfer}>确认转让</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除空间对话框 */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>删除空间</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 p-6">
            <div className="p-4 rounded-[10px] border border-[#FF5255] bg-[rgba(255,82,85,0.06)]">
              <p className="text-[#E23131]">
                警告：该工作空间及工作区下的内容，删除后将无法恢复，请谨慎操作！
              </p>
            </div>

            <div className="p-4 rounded-[10px] border border-[#FF5255] bg-[rgba(255,82,85,0.06)]">
              <p className="text-[#E23131]">
                该操作将永久删除「{currentWorkspace?.name}
                」工作空间的数据，为防止意外， 确认继续操作请输入以下内容:
              </p>
              <p className="mt-4 text-black font-bold">
                {currentWorkspace?.name}
              </p>
            </div>

            <Input
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              placeholder="请输入工作区名称"
              className="border-[#FF5255]"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false)
                setDeleteConfirmName('')
              }}
            >
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteConfirmName !== currentWorkspace?.name}
            >
              确认删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 退出空间对话框 */}
      <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>退出空间</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 p-6">
            <p className="text-m text-gray-700 text-center">
              退出后，您将无法查看和编辑该空间下的所有内容。
            </p>
            <p className="text-m text-black text-center">
              确定退出「{currentWorkspace?.name}」空间吗？
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handleLeave}>确认退出</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Setting
